import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Monitor01, Phone01, Spinner, Tabs } from "@opendate-app/setlist";

import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import { toastError } from "../../../../shared/toastHelper";
import { flattenLiquidData } from "../../../../shared/liquidUtils";
import TemplateHTMLPreview from "../../BeefreeTemplates/TemplateHTMLPreview";
import { parseLiquid } from "../../BeefreeTemplates/ErrorsContainer";

const ASSIGNED_STAFF = "Contact's assigned staff member";
const ASSIGNED_STAFF_LABEL = "Contact's relationship manager";

const containerStyle = {
  background: "var(--sl-bg-primary)",
  boxShadow: "-2px 1px 5px 0 rgba(0,0,0,0.1)",
  display: "grid",
  width: "100%",
};

const previewColumnStyle = {
  borderLeft: "1px solid var(--sl-border-subtle)",
  color: "var(--sl-text-primary)",
  display: "flex",
  flexDirection: "column",
  fontSize: "14px",
};

const detailRowStyle = {
  display: "flex",
  flexDirection: "row",
  gap: 4,
  borderBottom: "1px solid var(--sl-border-subtle)",
  padding: "10px",
};

const desktopViewerContainerStyle = {
  height: "60vh",
  maxHeight: "75%",
  overflow: "scroll",
  padding: "5px",
};

const desktopViewerStyle = {
  background: "#FFFFFF",
  height: "100%",
  width: "100%",
};

export const htmlContentStyle = {
  border: "none",
  height: "100%",
  overflow: "scroll",
  width: "100%",
};

const mobileViewerContainerStyle = {
  background: "var(--sl-bg-tertiary)",
  display: "grid",
  height: "60vh",
  padding: "10px",
  placeItems: "center",
};

const mobileViewerStyle = {
  background: "#FFFFFF",
  height: "100%",
  maxHeight: "60vh",
  overflow: "scroll",
  width: "320px",
};

const sampleDataPickerContainer = {
  padding: 15,
};

const headerStyle = {
  borderBottom: "1px solid var(--sl-border-subtle)",
  color: "var(--sl-text-quaternary)",
  fontSize: 10,
  paddingBottom: 5,
  width: "100%",
};

const rowStyle = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  textWrap: "nowrap",
  width: "100%",
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--sl-text-quaternary)",
};

const textStyle = {
  fontSize: 12,
  color: "var(--sl-text-quaternary)",
};

const valueStyle = {
  fontSize: 12,
  color: "var(--sl-fg-brand)",
};

const sampleDataColumnStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const PreviewTab = ({
  template,
  sampleData,
}) => {
  const {
    csrfToken,
    team,
    selectedNode,
    venueOwnership,
  } = useCustomCampaignShowContext();

  const [isLoading, setIsLoading] = useState(true);
  const [htmlContent, setHtmlContent] = useState("");
  const [viewer, setViewer] = useState("desktop");
  const [parsedTo, setParsedTo] = useState(selectedNode.node_data.to);
  const [parsedSubject, setParsedSubject] = useState(selectedNode.node_data.subject);
  const [parsedPreheader, setParsedPreheader] = useState(selectedNode.node_data.preheader);

  const flattenedSampleData = flattenLiquidData(sampleData);

  useEffect(() => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post("/teams/" + team.id + "/beefree_templates/" + selectedNode.beefree_template.id + "/render_liquid_html", {
      beefree_template: {
        template: template,
        sample_data: sampleData,
        venue_ownership_id: venueOwnership?.id,
      },
    })
      .then(({ data }) => {
        setHtmlContent(data.parsedText);
        setIsLoading(false);
      })
      .catch(() => toastError("Could not fetch email preview, please try again"));

    parseLiquid(selectedNode.node_data.subject, sampleData, team.id, selectedNode.beefree_template.id, csrfToken).then(({ parsedText }) => {
      setParsedSubject(parsedText);
    });

    parseLiquid(selectedNode.node_data.to, sampleData, team.id, selectedNode.beefree_template.id, csrfToken).then(({ parsedText }) => {
      setParsedTo(parsedText);
    });

    if (parsedPreheader) {
      parseLiquid(selectedNode.node_data.preheader, sampleData, team.id, selectedNode.beefree_template.id, csrfToken).then(({ parsedText }) => {
        setParsedPreheader(parsedText);
      });
    }
  }, [template]);

  return (
    isLoading ? (
      <Spinner size="md" aria-label="Loading email preview" />
    ) : (
      <div style={{
        ...containerStyle,
        gridTemplateColumns: flattenedSampleData.length ? "minmax(0, 1fr) minmax(0, 2fr)" : "minmax(0, 1fr)"
      }}>
        {flattenedSampleData.length ? (
          <div style={sampleDataPickerContainer}>
            <div style={sampleDataColumnStyle}>
              <div style={headerStyle}>
                ATTRIBUTES
              </div>
              {flattenedSampleData.map(({ path, value }) => (
                !!value ? (
                  <div key={path} style={rowStyle}>
                    <div style={textStyle}>{path}</div>
                    <div style={valueStyle}>{`"${value}"`}</div>
                  </div>
                ) : null
              ))}
            </div>
          </div>
        ) : null}
        <div style={previewColumnStyle}>
          <div style={detailRowStyle}>
            <Tabs
              variant="button-border"
              size="sm"
              collapseBelow={0}
              aria-label="Preview size"
              value={viewer}
              onChange={setViewer}
              items={[
                { value: "desktop", label: <><Monitor01 width={16} height={16} /> Desktop</> },
                { value: "mobile", label: <><Phone01 width={16} height={16} /> Mobile</> },
              ]}
            />
          </div>
          <div style={detailRowStyle}>
            <div style={labelStyle}>From:</div>
            <div style={textStyle}>{selectedNode.beefree_template.email_send_domain?.name_with_address}</div>
          </div>
          <div style={detailRowStyle}>
            <div style={labelStyle}>To:</div>
            <div style={textStyle}>{selectedNode.node_data.recipient_type === ASSIGNED_STAFF ? ASSIGNED_STAFF_LABEL : parsedTo}</div>
          </div>
          <div style={detailRowStyle}>
            <div style={labelStyle}>Subject:</div>
            <div style={textStyle}>{parsedSubject}</div>
          </div>
          {parsedPreheader ? (
            <div style={detailRowStyle}>
              <div style={labelStyle}>Preheader:</div>
              <div style={textStyle}>{parsedPreheader}</div>
            </div>
          ) : null}
          <div style={viewer === "desktop" ? desktopViewerContainerStyle : mobileViewerContainerStyle}>
            <div style={viewer === "desktop" ? desktopViewerStyle : mobileViewerStyle}>
              <TemplateHTMLPreview
                htmlContent={htmlContent}
                style={htmlContentStyle}
              />
            </div>
          </div>
        </div>
      </div>
    )
  );
};

PreviewTab.propTypes = {
  template: PropTypes.string,
  sampleData: PropTypes.object,
};

PreviewTab.defaultProps = {
  template: null,
  sampleData: null,
};

export default PreviewTab;
